import { useContext, useEffect, useRef, useState } from 'react';
import CompaniesContext from '../context/CompaniesContext';
import { useYearTypeContext } from '../context/YearTypeContext';
import { YearType } from '../services/types';

interface AggregatedIndustryData {
  [industryName: string]: number;
}

const getYearRange = (year: string, yearType: YearType): [Date, Date] => {
  const selectedYear = Number(year);

  switch (yearType.toLowerCase()) {
    case 'q1':
      // Calendar year
      return [new Date(selectedYear, 0, 1), new Date(selectedYear, 11, 31, 23, 59, 59, 999)];

    case 'q2':
      // Fiscal year: Apr 1 (prev year) – Mar 31 (this year)
      return [
        new Date(selectedYear - 1, 3, 1), // April 1 of previous year
        new Date(selectedYear, 2, 31, 23, 59, 59, 999), // March 31 of current year
      ];

    case 'q3':
      // Jul 1 – Jun 30
      return [
        new Date(selectedYear - 1, 6, 1), // July 1 of previous year
        new Date(selectedYear, 5, 30, 23, 59, 59, 999), // June 30 of current year
      ];

    case 'q4':
      // Oct 1 – Sep 30
      return [
        new Date(selectedYear - 1, 9, 1), // October 1 of previous year
        new Date(selectedYear, 8, 30, 23, 59, 59, 999), // September 30 of current year
      ];

    default:
      // fallback to calendar year
      return [new Date(selectedYear, 0, 1), new Date(selectedYear, 11, 31, 23, 59, 59, 999)];
  }
};

const getQuarterBounds = (
  quarter: string
): [number, number] => {
  const normalizedQuarter = quarter.toLowerCase(); // e.g., 'Q1' → 'q1'

  const quarterMap: Record<string, [number, number]> = {
    q1: [0, 2],   // Jan-Mar
    q2: [3, 5],   // Apr-Jun
    q3: [6, 8],   // Jul-Sep
    q4: [9, 11],  // Oct-Dec
  };

  return quarterMap[normalizedQuarter];
};

export const useIndustryInteractionCounts = (
  year: string,
  quarter: YearType | string
) => {
    const { companies } = useContext(CompaniesContext);
    const { yearType } = useYearTypeContext();
    const [industryCounts, setIndustryCounts] = useState<AggregatedIndustryData>({});
    const [loading, setLoading] = useState(true);

    const cache = useRef<Map<number, any>>(new Map()); // In-memory cache

    useEffect(() => {
        if (!year || !yearType) return;
        const [startDate, endDate] = getYearRange(year, yearType);
        const quarterBounds = getQuarterBounds(quarter);

        const fetchCompanyDetails = async (companyId: number): Promise<any | null> => {
        if (cache.current.has(companyId)) {
            return cache.current.get(companyId);
        }

        try {
            const res = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getcompanydetails?companyId=${companyId}`);
            const data = await res.json();
            const companyData = data?.data;
            if (companyData) {
            cache.current.set(companyId, companyData);
            return companyData;
            }
        } catch (err) {
            console.error(`Failed to fetch details for company ${companyId}:`, err);
        }

        return null;
        };

        const run = async () => {
        setLoading(true);
        const counts: AggregatedIndustryData = {};

        const fetches = companies.map(async (company) => {
            // ✅ Skip companies with no industry name or 0 interactions
            if (company.total_interactions === 0 || !company.industry?.name) return;

            const companyData = await fetchCompanyDetails(company.id);
            if (!companyData) return;

            const interactions = companyData.interactions || [];

            const validInteractions = interactions.filter((i: any) => {
            const date = new Date(i.interaction_date);
            if (date < startDate || date > endDate) return false;

            if (quarterBounds) {
                const month = date.getMonth();
                return month >= quarterBounds[0] && month <= quarterBounds[1];
            }

            return true;
            });

            const industryName = company.industry?.name || 'Unknown';

            if (!counts[industryName]) counts[industryName] = 0;
            counts[industryName] += validInteractions.length;
        });

        await Promise.all(fetches);

        setIndustryCounts(counts);
        setLoading(false);
        };

        if (companies.length > 0) {
          run();
        }
    }, [companies, year, quarter, yearType]);

    return { industryCounts, loading };
};
